import { SubLoginService } from './../../api/services/sub-login.service';
import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  AfterViewInit,
  ChangeDetectorRef,
} from '@angular/core';
import * as XLSX from 'xlsx';
import { Plugin, Chart } from 'chart.js';
import { toFont } from 'chart.js/helpers';
import {
  AnnualConsumptionLineResult,
  BaseStatsLineResult,
  DeliveryAddress,
  HomologousConsumptionResponse,
  HomologousPeriod,
  SearchStatsModel,
  SearchStatsResponseModel,
  StatisticsViewModel,
  TbLaboratorio,
  TbProduto,
  TbSub,
  PaginatedDetailedConsumptionRequest,
  PaginatedDetailedConsumptionResponse,
} from 'src/app/api/models/statistics';
import { StatisticsService } from 'src/app/api/services/statistics.service';
import { SnackBarService } from 'src/app/api/services/snack-bar.service';
import { PerfilService } from 'src/app/api/services/perfil.service';
import { ProfileDataRequest } from 'src/app/api/models/perfil';
import { Observable, Subscription, map, startWith, firstValueFrom } from 'rxjs';
import { Address, User } from 'src/app/api/models/getUsers';
import { FormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { EncryptionService } from 'src/app/api/services/encryption.service';
import { StorageKeys } from 'src/app/api/models/storageKeys';


@Component({
  selector: 'app-statistics',
  templateUrl: './statistics.component.html',
  styleUrls: ['./statistics.component.css'],
  standalone: false,
})
export class StatisticsComponent implements OnDestroy, AfterViewInit, OnInit {
  loggedUser = this.perfilService.getCurrentUserInfo();
  clientSelected: User | undefined;
  addressSelected: Address | undefined;
  isMenuOpen = false;
  isModalOpen: boolean = false;
  isLoading: boolean = false;
  isViewModelLoaded: boolean = false;
  isTableLoading: boolean = false;

  currentYearStart = `${new Date().getFullYear()}-01-01`;
  todayDate = new Date().toISOString().split('T')[0];

  // ── Annual consumption chart is always expanded ──────────────────────────
  isAnnualConsumption: boolean = true;

  // ── Top 10 charts ─────────────────────────────────────────────────────────
  isTop10Products: boolean = false;
  isTop10Labs: boolean = false;

  // ── Consumption detail table (open by default in vista simples) ───────────
  consumptionViewMode: 'simples' | 'completa' = 'simples';

  // ── Product / Lab filter state ────────────────────────────────────────────
  activeProduct: string = '';
  activeLaboratory: string = '';
  activeLaboratoryName: string = '';
  activeSubstance: string = '';
  activeSubstanceName: string = '';
  substanceTableVisibility: boolean = false;

  // ── Período homólogo (comparison column on the annual chart) ────────────
  // null = chart shows only the current period (default on load).
  homologoPeriod: HomologousPeriod | null = null;
  homologoData?: HomologousConsumptionResponse;
  isHomologoLoading: boolean = false;
  /** Per-search cache so switching N-1 ↔ N-2 doesn't refetch. Reset on every search. */
  private homologoCache: Partial<Record<HomologousPeriod, HomologousConsumptionResponse>> = {};
  private homologoSub?: Subscription;

  /** Series colours of the annual chart (RGB triplets, used with alpha for the fade) */
  private readonly CURRENT_SERIES_RGB = '94, 196, 195';
  private readonly HOMOLOGO_SERIES_RGB = '247, 148, 52';

  // ── Summary values shown above the annual chart ───────────────────────────
  totalPurchasesValue: number = 0;

  // ── Search results & view model ──────────────────────────────────────────
  statsSearchResults?: SearchStatsResponseModel;
  statsViewModel!: StatisticsViewModel;
  profileRequest!: ProfileDataRequest;
  searchInterval: string = '';
  filteredOptions!: Observable<User[]>;
  myControl = new FormControl<string | User>('');
  users: User[] = [];

  previousSearchData: SearchStatsModel = {
    userId: this.loggedUser.userId,
    clientEntityId: this.loggedUser.clientId,
    productId: '',
    productIds: [],
    laboratoryId: '',
    substanceId: '',
    addressId: '',
    startDate: '',
    endDate: '',
    year: '',
    subClientId: '',
  };

  productList: TbProduto[] = [];
  sessionClientNumber = this.encryptSvc.getDecryptedItem(StorageKeys.BIO2_CLIENT_ID)!;
  modalParameter: string = '';
  observerConfig = { attributes: true };

  // ── Chart instances ───────────────────────────────────────────────────────
  consumptionChart: any;
  top10ProductsChart: any;
  top10LabsChart: any;
  ctx: any;
  top10ProductsCtx: any;
  top10LabsCtx: any;

  unsubscribe: Subscription = new Subscription();

  private searchStatsSub?: Subscription;
  private tablePageSub?: Subscription;

  // ── Table columns ─────────────────────────────────────────────────────────
  displayedColumns: string[] = [
    'designation',
    'quantityCashed',
    'quantityBonus',
    'valueCashed',
  ];

  // ── Top-10 color palettes ─────────────────────────────────────────────────
  /** Blue → beige gradient for Top 10 Products */
  private readonly TOP_PRODUCTS_COLORS: string[] = [
    '#00AEAE', '#1DB8B8', '#3AC2C2', '#57CCCC', '#74D6D6',
    '#91E0E0', '#AEEAEA', '#C5EDED', '#D8F0F0', '#EEF8F8',
  ];

  /** Green → beige gradient for Top 10 Labs */
  private readonly TOP_LABS_COLORS: string[] = [
    '#2E7D32', '#388E3C', '#43A047', '#66BB6A', '#81C784',
    '#A5D6A7', '#C8E6C9', '#DCEDC8', '#F1F8E9', '#F9FBE7',
  ];

  // Pagination
  currentPage: number = 1;
  pageSize: number = 10;

  /** Formato monetário usado em toda a página: sem separador de milhares, vírgula nos decimais. */
  formatMoney(value: number | null | undefined): string {
    return (value ?? 0).toFixed(2).replace('.', ',') + '€';
  }

  /** Discount percentage: "15%", "12,5%"; "—" when unknown (rows imported before the field existed). */
  formatPercent(value: number | null | undefined): string {
    if (value == null) return '—';
    const digits = Number.isInteger(value) ? 0 : 2;
    return value.toFixed(digits).replace('.', ',') + '%';
  }

  constructor(
    private perfilService: PerfilService,
    private statisticsService: StatisticsService,
    private snackBarService: SnackBarService,
    private SubLoginService: SubLoginService,
    private encryptSvc: EncryptionService,
    private cdr: ChangeDetectorRef
  ) { }

  @ViewChild('top10ProductsChart') top10ProductsChartRef?: ElementRef<HTMLCanvasElement>;
  @ViewChild('top10LabsChart') top10LabsChartRef?: ElementRef<HTMLCanvasElement>;
  @ViewChild('consumptionChart') consumptionChartRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('consumptionChartScroll') consumptionChartScrollRef?: ElementRef<HTMLDivElement>;
  @ViewChild('consumptionYAxis') consumptionYAxisRef?: ElementRef<HTMLCanvasElement>;

  canvasElement: any;

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnInit() {
    this.GetStatisticsViewModel();
  }

  ngAfterViewInit() {
    // Annual consumption chart
    this.canvasElement = this.consumptionChartRef.nativeElement;
    this.canvasElement.style.display = '';
    this.ctx = this.canvasElement.getContext('2d');

    this._preventChartDisplayOverride(this.canvasElement);

    // ── Inline plugin: value labels above every non-zero bar ────────────────
    // Works for one or two visible series (current + período homólogo).
    // Anti-collision: labels are placed left-to-right; each one tries "just
    // above its bar", then one row higher at a time, then just inside the top
    // of the bar, and keeps the first slot that doesn't overlap a label that
    // was already placed.
    const consumptionDataLabels: Plugin<'bar'> = {
      id: 'consumptionDataLabels',
      afterDatasetsDraw(chart) {
        const { ctx } = chart;
        const visibleIdx = chart.data.datasets
          .map((_, i) => i)
          .filter((i) => chart.isDatasetVisible(i));
        if (!visibleIdx.length) return;

        const comparing = visibleIdx.length > 1;
        const fontSize = comparing ? 11 : 13;
        const baseOffset = comparing ? 6 : 10;
        const labelHeight = comparing ? 14 : 16;
        const horizontalPad = comparing ? 4 : 6;
        const maxRowsUp = 4;
        const minTop = 2; // labels may use the layout padding, never leave the canvas

        ctx.save();
        ctx.font = `600 ${fontSize}px Poppins, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';

        type LabelItem = { x: number; top: number; base: number; text: string; color: string };
        const items: LabelItem[] = [];
        visibleIdx.forEach((dsIndex) => {
          const meta = chart.getDatasetMeta(dsIndex);
          const values = (chart.data.datasets[dsIndex]?.data ?? []) as number[];
          values.forEach((value, index) => {
            if (!value || value <= 0) return;
            const el: any = meta.data[index];
            if (!el) return;
            items.push({
              x: el.x,
              top: el.y,
              base: el.base,
              text: value.toFixed(2).replace('.', ',') + '€',
              color: dsIndex === 0 ? '#2d4a4b' : '#b8641a',
            });
          });
        });
        items.sort((a, b) => a.x - b.x);

        type PlacedLabel = { x: number; y: number; halfW: number };
        const placed: PlacedLabel[] = [];
        const overlaps = (x: number, y: number, halfW: number) =>
          placed.some(
            (p) =>
              Math.abs(p.x - x) < p.halfW + halfW + horizontalPad &&
              Math.abs(p.y - y) < labelHeight
          );

        items.forEach((item) => {
          const halfW = ctx.measureText(item.text).width / 2;
          // Keep edge labels (first / last month) inside the canvas
          const x = Math.min(Math.max(item.x, halfW + 2), chart.width - halfW - 2);

          // Candidate baselines (textBaseline:'bottom' → visual centre is y - labelHeight/2)
          const candidates: number[] = [];
          for (let row = 0; row < maxRowsUp; row++) {
            candidates.push(item.top - baseOffset - row * (labelHeight + 2));
          }
          const inside = item.top + labelHeight + 4;
          if (inside < item.base - 2) candidates.push(inside);

          const fits = (y: number) => y - labelHeight >= minTop;
          let y = candidates.find((c) => fits(c) && !overlaps(x, c - labelHeight / 2, halfW));
          // No free slot: use the highest row that still fits the canvas
          if (y === undefined) {
            y = candidates.slice(0, maxRowsUp).reverse().find(fits) ?? candidates[0];
          }

          ctx.fillStyle = item.color;
          ctx.fillText(item.text, x, y);
          placed.push({ x, y: y - labelHeight / 2, halfW });
        });

        ctx.restore();
      },
    };

    // ── Inline plugin: sticky y-axis while the chart scrolls horizontally ───
    const stickyYAxis: Plugin<'bar'> = {
      id: 'stickyYAxis',
      afterRender: (chart) => this.SyncStickyYAxis(chart),
    };

    // ── Map abbreviated month prefixes → full Portuguese names ───────────────
    const MONTH_FULL_NAMES: Record<string, string> = {
      Jan: 'Janeiro', Fev: 'Fevereiro', Mar: 'Março', Abr: 'Abril',
      Mai: 'Maio', Jun: 'Junho', Jul: 'Julho', Ago: 'Agosto',
      Set: 'Setembro', Out: 'Outubro', Nov: 'Novembro', Dez: 'Dezembro',
    };

    // ── Per-bar vertical fade ────────────────────────────────────────────────
    // Each bar gets its own gradient running from its top edge down to the
    // zero line, so every bar shows the full fade regardless of its height.
    // Scriptable: resolved by Chart.js at render time, once scales are sized.
    const barFade = (context: any, rgb: string, alphas: [number, number, number]) => {
      const chart = context.chart;
      const { ctx, chartArea, scales } = chart;
      if (!chartArea || !scales?.y) return 'transparent';
      const value = Number(context.raw ?? context.parsed?.y ?? 0);
      const top = scales.y.getPixelForValue(Math.max(value, 0));
      const base = scales.y.getPixelForValue(0);
      if (!isFinite(top) || !isFinite(base) || base - top < 1) {
        return `rgba(${rgb}, ${alphas[0]})`;
      }
      const gradient = ctx.createLinearGradient(0, top, 0, base);
      gradient.addColorStop(0, `rgba(${rgb}, ${alphas[0]})`);
      gradient.addColorStop(0.6, `rgba(${rgb}, ${alphas[1]})`);
      gradient.addColorStop(1, `rgba(${rgb}, ${alphas[2]})`);
      return gradient;
    };

    // Shared bar styling; only the colour changes between series
    const barSeries = (rgb: string) => ({
      data: [] as number[],
      borderColor: `rgba(${rgb}, 1)`,
      // Solid cap on top of each bar
      borderWidth: { top: 2, right: 0, bottom: 0, left: 0 },
      borderSkipped: false as const,
      borderRadius: { topLeft: 6, topRight: 6, bottomLeft: 0, bottomRight: 0 },
      backgroundColor: (context: any) => barFade(context, rgb, [0.55, 0.18, 0.06]),
      // Slightly stronger fade on hover to highlight the active month
      hoverBackgroundColor: (context: any) => barFade(context, rgb, [0.8, 0.35, 0.12]),
      hoverBorderColor: `rgba(${rgb}, 1)`,
      categoryPercentage: 0.7,
      barPercentage: 0.9,
      maxBarThickness: 56,
    });

    this.consumptionChart = new Chart(this.ctx, {
      type: 'bar',
      plugins: [consumptionDataLabels, stickyYAxis],
      data: {
        labels: [],
        datasets: [
          // [0] Current period — always visible
          { label: '', ...barSeries(this.CURRENT_SERIES_RGB) },
          // [1] Período homólogo (N-1 / N-2) — hidden until the user picks one
          { label: '', hidden: true, ...barSeries(this.HOMOLOGO_SERIES_RGB) },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: { top: 36, bottom: 8 },
        },
        interaction: {
          mode: 'index',
          intersect: false,
        },
        plugins: {
          legend: { display: false },
          title: { display: false },
          tooltip: {
            displayColors: false,
            callbacks: {
              title: (items) => {
                const raw = items[0]?.label ?? '';
                // Strip trailing year (4-digit "Jan2025" or 2-digit "Jan/25") to
                // resolve the month abbreviation, then append the year back when
                // the label carried it.
                const fullYearMatch = raw.match(/(\d{4})$/);
                const shortYearMatch = raw.match(/\/(\d{2})$/);
                const abbr = raw.replace(/\/?\d{2,4}$/, '').trim();
                const full = MONTH_FULL_NAMES[abbr] ?? raw;
                // Comparing: the year goes on each series line instead
                if (this.isHomologoVisible) return full;
                if (fullYearMatch) return `${full} ${fullYearMatch[1]}`;
                if (shortYearMatch) return `${full} 20${shortYearMatch[1]}`;
                return full;
              },
              label: (ctx) => {
                const value = this.formatMoney(ctx.parsed.y);
                if (!this.isHomologoVisible) return value;
                const year = this.chartPointYear(ctx.datasetIndex, ctx.dataIndex);
                return `${year || ctx.dataset.label}: ${value}`;
              },
              labelColor: (ctx) => {
                const rgb = ctx.datasetIndex === 0 ? this.CURRENT_SERIES_RGB : this.HOMOLOGO_SERIES_RGB;
                return {
                  borderColor: `rgba(${rgb}, 1)`,
                  backgroundColor: `rgba(${rgb}, 1)`,
                  borderWidth: 0,
                  borderRadius: 2,
                };
              },
              footer: (items) => {
                if (!this.isHomologoVisible) return '';
                const current = items.find((i) => i.datasetIndex === 0)?.parsed.y;
                const previous = items.find((i) => i.datasetIndex === 1)?.parsed.y;
                const variation = this.formatVariation(current, previous);
                return variation ? `Variação: ${variation}` : '';
              },
            },
          },
        },
        scales: {
          x: {
            grid: { color: 'rgba(0,0,0,0.06)', lineWidth: 1 },
            ticks: {
              font: { family: 'Poppins, sans-serif', size: 13 },
              color: '#8a9e9f',
              maxRotation: 0,
            },
            border: { display: false },
          },
          y: {
            beginAtZero: true,
            grace: '25%',
            grid: { color: 'rgba(0,0,0,0.06)', lineWidth: 1 },
            border: { display: false },
            ticks: {
              font: { family: 'Poppins, sans-serif', size: 12 },
              color: '#8a9e9f',
              callback: (value) => Number(value).toFixed(2).replace('.', ',') + '€',
            },
          },
        },
      },
    });

    // ── Top 10 Products pie chart ────────────────────────────────────────────
    if (this.top10ProductsChartRef) {
      const el = this.top10ProductsChartRef.nativeElement;
      el.style.display = '';
      this.top10ProductsCtx = el.getContext('2d');
      this._preventChartDisplayOverride(el);

      this.top10ProductsChart = new Chart(this.top10ProductsCtx, {
        type: 'pie',
        data: { labels: [], datasets: [{ data: [], backgroundColor: this.TOP_PRODUCTS_COLORS }] },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            title: { display: false },
          },
        },
      });
    }

    // ── Top 10 Labs pie chart ────────────────────────────────────────────────
    if (this.top10LabsChartRef) {
      const el = this.top10LabsChartRef.nativeElement;
      el.style.display = '';
      this.top10LabsCtx = el.getContext('2d');
      this._preventChartDisplayOverride(el);

      this.top10LabsChart = new Chart(this.top10LabsCtx, {
        type: 'pie',
        data: { labels: [], datasets: [{ data: [], backgroundColor: this.TOP_LABS_COLORS }] },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            title: { display: false },
          },
        },
      });
    }

    this.cdr.detectChanges();

    // ── Autocomplete filter ──────────────────────────────────────────────────
    this.filteredOptions = this.myControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        const name = typeof value === 'string' ? value : value?.name;
        return name ? this._filter(name as string) : this.users.slice();
      })
    );
  }

  // ── Data loading ──────────────────────────────────────────────────────────

  /**
   * Resolves the identifier sent to the BE for stats lookup:
   * - Laboratorio: lab id (username without the leading 'l').
   * - Cliente + K-prefixed username: UtilizadorPortal, so the BE can pick the
   *   correct registo when several share the same NCliente (group leader case).
   * - Everyone else (regular Cliente, SuperClient, Administrador): NCliente.
   */
  private resolveStatsLookupId(): string {
    const role = this.loggedUser.role;
    const username = this.loggedUser.username ?? '';

    if (role === 'Laboratorio') {
      return username.slice(1);
    }

    if (role === 'Cliente' && username.toLowerCase().startsWith('k')) {
      return username;
    }

    return this.loggedUser.clientId;
  }

  private resolveStatsSubClientId(): string {
    if (this.loggedUser.role !== 'Laboratorio') {
      return '';
    }

    return this.loggedUser.clientId;
  }

  GetStatisticsViewModel() {
    this.isLoading = true;
    this.unsubscribe.add(
      this.statisticsService
        .GetStatisticsViewModel(this.resolveStatsLookupId(), this.resolveStatsSubClientId())
        .subscribe({
          next: (response: StatisticsViewModel) => {
            this.statsViewModel = response;
            this.isViewModelLoaded = true;

            // Group leaders arrancam directamente em vista completa.
            const isGroupLeader = !!this.statsViewModel?.group;
            if (isGroupLeader) {
              this.consumptionViewMode = 'completa';
            }

            const defaultSearch: SearchStatsModel = {
              userId: this.loggedUser.userId,
              clientEntityId: this.loggedUser.clientId,
              productId: '',
              laboratoryId: '',
              substanceId: '',
              addressId: '',
              startDate: '',
              endDate: '',
              year: new Date().getFullYear().toString(),
              subClientId: this.resolveStatsSubClientId(),
              viewType: isGroupLeader ? 'complete' : 'simple',
            };
            this.SearchStats(defaultSearch);
          },
          error: (error: unknown) => {
            this.isLoading = false;
            console.error('Error:', error);
          },
        })
    );
  }

  SearchStats(searchData: SearchStatsModel) {
    this.isLoading = true;

    // Reset to page 1 whenever the caller didn't specify a page
    if (!searchData.pageNumber) {
      this.currentPage = 1;
      searchData.pageNumber = 1;
      searchData.pageSize = this.pageSize;
    }

    // The filters panel (stats-filter.component) has no knowledge of which table view
    // (simples/completa) is currently active, so it never sets viewType. Without this,
    // applying a filter while in "vista completa" silently falls back to a simple-view
    // query, leaving Data doc./Nº doc./Morada LE/Localidade LE blank until the user
    // manually toggles the view (which re-fetches via FetchTablePage, setting viewType
    // correctly). Always derive it here from the current view mode instead.
    searchData.viewType = this.consumptionViewMode === 'completa' ? 'complete' : 'simple';

    this.previousSearchData = searchData;

    try {
      // Track active filters — treat any selected product (single or multi) as "has product filter"
      this.activeProduct = (searchData.productIds && searchData.productIds.length)
        ? searchData.productIds.join(',')
        : (searchData.productId ?? '');
      this.activeLaboratory = searchData.laboratoryId ?? '';
      this.activeLaboratoryName = searchData.laboratoryId
        ? this.statsViewModel.laboratoriesList?.find(
            (lab) => lab.id === searchData.laboratoryId
          )?.labName || ''
        : '';

      if (searchData.substanceId) {
        this.activeSubstance = searchData.substanceId;
        this.activeSubstanceName =
          this.statsViewModel.substancesList?.find(
            (sub) => sub.idSubs === searchData.substanceId
          )?.nome || '';
        this.substanceTableVisibility = true;
      } else {
        this.substanceTableVisibility = false;
        this.activeSubstance = '';
      }

      this.searchStatsSub?.unsubscribe();
      this.searchStatsSub = this.statisticsService.SearchStats(searchData).subscribe({
        next: (data: SearchStatsResponseModel) => {
          this.statsSearchResults = data;
          if (this.statsSearchResults) {
            this.LoadStatsData(this.statsSearchResults);
          }
        },
        error: (error: unknown) => {
          this.isLoading = false;
          console.error('Error:', error);
        },
        complete: () => {
          this.isLoading = false;
          this.isMenuOpen = true;
        },
      });
    } catch (error) {
      this.isLoading = false;
      this.snackBarService.openSnackBar(
        'Erro ao pesquisar por estatísticas',
        '#F97066',
        '../../../assets/Icons/ErrorIcon.png'
      );
    }
  }

  LoadStatsData(data: SearchStatsResponseModel) {
    // ── Summary values above chart ─────────────────────────────────────────
    if (data.totalPurchasesValue != null) {
      this.totalPurchasesValue = data.totalPurchasesValue;
    } else {
      this.totalPurchasesValue =
        data.annualConsumptionResults?.reduce((sum, m) => sum + m.amount, 0) ?? 0;
    }

    // ── Annual consumption bar chart ───────────────────────────────────────
    // New search → drop any cached comparison. The chart first renders with the
    // current period only; if the user had a período homólogo selected, its
    // column is refetched for the new filters and added once it arrives.
    this.homologoSub?.unsubscribe();
    this.homologoCache = {};
    this.homologoData = undefined;
    this.isHomologoLoading = false;
    this.RenderConsumptionChart();
    if (this.homologoPeriod) this.LoadHomologoData(this.homologoPeriod);

    // ── Top 10 Products pie chart ──────────────────────────────────────────
    {
      this.RemoveChartData(this.top10ProductsChart);

      const top10Products: BaseStatsLineResult[] = JSON.parse(
        JSON.stringify(data.topProductsResults ?? [])
      )
        .sort((a: BaseStatsLineResult, b: BaseStatsLineResult) => b.valueCashed - a.valueCashed)
        .slice(0, 10);

      this.AddChartData(
        this.top10ProductsChart,
        top10Products.map((p) => p.designation),
        {
          label: 'Valor',
          data: top10Products.map((p) => parseFloat(p.valueCashed.toString())),
          backgroundColor: this.TOP_PRODUCTS_COLORS,
        }
      );

      // ── Top 10 Labs pie chart ──────────────────────────────────────────
      this.RemoveChartData(this.top10LabsChart);

      const top10Labs: { designation: string; valueCashed: number }[] = JSON.parse(
        JSON.stringify(data.topLaboratoriesResults ?? [])
      )
        .map((l: BaseStatsLineResult) => ({
          designation: l.laboratoryName ?? l.designation,
          valueCashed: l.valueCashed,
        }))
        .sort((a: { valueCashed: number }, b: { valueCashed: number }) => b.valueCashed - a.valueCashed)
        .slice(0, 10);

      this.AddChartData(
        this.top10LabsChart,
        top10Labs.map((l) => l.designation),
        {
          label: 'Valor',
          data: top10Labs.map((l) => parseFloat(l.valueCashed.toString())),
          backgroundColor: this.TOP_LABS_COLORS,
        }
      );
    }

  }

  // ── Annual chart rendering + período homólogo ─────────────────────────────

  /**
   * Pushes the current series (and the período homólogo, when selected and
   * loaded) into the annual chart. Styling set in ngAfterViewInit is kept;
   * only labels, data and the comparison dataset's visibility change.
   */
  private RenderConsumptionChart() {
    const chart = this.consumptionChart;
    if (!chart) return;

    const current: AnnualConsumptionLineResult[] =
      this.statsSearchResults?.annualConsumptionResults ?? [];

    // When the selected range spans more than one year (e.g. Dez 2024 → Fev 2025)
    // we keep a /YY suffix on the x-axis label to disambiguate the months.
    const multiYear = this.yearsOf(current).length > 1;
    chart.data.labels = current.map((c) => {
      const abbr = c.monthOfTheYear.replace(/\d{4}$/, '');
      const yr = (c.monthOfTheYear.match(/\d{4}$/) ?? [''])[0];
      return multiYear && yr ? `${abbr}/${yr.slice(-2)}` : abbr;
    });

    const [currentDs, homologoDs] = chart.data.datasets;
    currentDs.label = this.currentSeriesLabel;
    currentDs.data = current.map((c) => parseFloat(c.amount.toString()));

    // Months are paired by index (the backend shifts the whole range by N years)
    const comparing = this.isHomologoVisible;
    const previous = comparing ? this.homologoData!.monthlyResults : [];
    homologoDs.label = this.homologoSeriesLabel;
    homologoDs.data = current.map((_, i) => parseFloat((previous[i]?.amount ?? 0).toString()));
    homologoDs.hidden = !comparing;

    // Two series → more label rows above the bars, and colour keys in the tooltip
    chart.options.layout.padding = { top: comparing ? 56 : 36, bottom: 8 };
    chart.options.plugins.tooltip.displayColors = comparing;

    chart.update();
  }

  // ── Horizontal scroll on narrow screens ───────────────────────────────────

  /** Minimum width per month column (px), so bars and value labels stay readable. */
  private readonly MIN_MONTH_WIDTH = 56;
  private readonly MIN_MONTH_WIDTH_COMPARING = 80;
  /** Room reserved for the y-axis tick labels (px). */
  private readonly Y_AXIS_RESERVE = 72;

  /**
   * Minimum width of the chart content. On wide screens the chart just fills
   * the card; below this width the card scrolls horizontally.
   */
  get consumptionChartMinWidth(): number {
    const months = this.statsSearchResults?.annualConsumptionResults?.length || 12;
    const perMonth = this.isHomologoVisible ? this.MIN_MONTH_WIDTH_COMPARING : this.MIN_MONTH_WIDTH;
    return this.Y_AXIS_RESERVE + months * perMonth;
  }

  /**
   * Redraws the y-axis tick labels on the sticky overlay canvas so the € scale
   * stays visible while the months scroll. Only shown once the user has
   * scrolled: at scrollLeft 0 the chart's own axis is visible, and the overlay
   * would just cover the first month's value label.
   */
  private SyncStickyYAxis(chart: any) {
    const overlay = this.consumptionYAxisRef?.nativeElement;
    const scroll = this.consumptionChartScrollRef?.nativeElement;
    const yScale = chart?.scales?.y;
    if (!overlay || !scroll || !yScale || !chart.chartArea) return;

    const scrollable = scroll.scrollWidth > scroll.clientWidth + 1;
    if (!scrollable) {
      overlay.style.display = 'none';
      return;
    }

    const ratio = chart.currentDevicePixelRatio || window.devicePixelRatio || 1;
    const width = Math.ceil(chart.chartArea.left);
    const height = chart.height;

    overlay.width = width * ratio;
    overlay.height = height * ratio;
    overlay.style.width = `${width}px`;
    overlay.style.height = `${height}px`;

    const ctx = overlay.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.fillStyle = '#f5f8f3'; // card background
    ctx.fillRect(0, 0, width, height);

    // Same font, colour and position as the chart's own y-axis ticks
    // (read from the resolved scale options so the overlay can't drift).
    const tickOpts = yScale.options.ticks ?? {};
    const gridOpts = yScale.options.grid ?? {};
    const tickOffset = (gridOpts.drawTicks === false ? 0 : gridOpts.tickLength ?? 8) +
      (tickOpts.padding ?? 3);
    ctx.font = toFont(tickOpts.font, chart.options.font).string;
    ctx.fillStyle = typeof tickOpts.color === 'string' ? tickOpts.color : '#8a9e9f';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    (yScale.ticks ?? []).forEach((tick: any, i: number) => {
      const label = Array.isArray(tick.label) ? tick.label.join(' ') : String(tick.label ?? '');
      ctx.fillText(label, yScale.right - tickOffset, yScale.getPixelForTick(i));
    });

    this.UpdateStickyYAxisVisibility();
  }

  /** Sticky y-axis only while the chart overflows AND is scrolled away from the start. */
  UpdateStickyYAxisVisibility() {
    const overlay = this.consumptionYAxisRef?.nativeElement;
    const scroll = this.consumptionChartScrollRef?.nativeElement;
    if (!overlay || !scroll) return;
    const scrollable = scroll.scrollWidth > scroll.clientWidth + 1;
    overlay.style.display = scrollable && scroll.scrollLeft > 0 ? 'block' : 'none';
  }

  /** Selects (or clears, with null / re-click) the período homólogo shown on the chart. */
  onHomologoPeriodChange(period: HomologousPeriod | null) {
    if (period === this.homologoPeriod) period = null;

    this.homologoPeriod = period;
    this.homologoSub?.unsubscribe();
    this.isHomologoLoading = false;
    this.homologoData = period ? this.homologoCache[period] : undefined;
    this.RenderConsumptionChart();

    if (period && !this.homologoData) this.LoadHomologoData(period);
  }

  private LoadHomologoData(period: HomologousPeriod) {
    const cached = this.homologoCache[period];
    if (cached) {
      this.homologoData = cached;
      this.RenderConsumptionChart();
      return;
    }

    // Same filters as the current search, only the period changes
    const request: SearchStatsModel = {
      ...this.previousSearchData,
      productIds: [...(this.previousSearchData.productIds ?? [])],
      pageNumber: undefined,
      pageSize: undefined,
      homologousPeriod: period,
    };

    this.isHomologoLoading = true;
    this.homologoSub?.unsubscribe();
    this.homologoSub = this.statisticsService.GetHomologousConsumption(request).subscribe({
      next: (data: HomologousConsumptionResponse) => {
        this.homologoCache[period] = data;
        if (this.homologoPeriod !== period) return;
        this.homologoData = data;
        this.RenderConsumptionChart();
      },
      error: (error: unknown) => {
        console.error('Error fetching período homólogo:', error);
        this.isHomologoLoading = false;
        this.homologoPeriod = null;
        this.homologoData = undefined;
        this.RenderConsumptionChart();
        this.snackBarService.openSnackBar(
          'Não foi possível carregar o período homólogo',
          '#F97066',
          '../../../assets/Icons/ErrorIcon.png'
        );
      },
      complete: () => {
        this.isHomologoLoading = false;
      },
    });
  }

  /** True when the comparison column is selected and its data is loaded. */
  get isHomologoVisible(): boolean {
    return !!this.homologoPeriod && !!this.homologoData?.monthlyResults?.length;
  }

  /** e.g. "2026", or "2024/25" when the range crosses a year boundary */
  get currentSeriesLabel(): string {
    return this.seriesLabel(this.statsSearchResults?.annualConsumptionResults) || 'Atual';
  }

  get homologoSeriesLabel(): string {
    return this.seriesLabel(this.homologoData?.monthlyResults) || (this.homologoPeriod ?? '');
  }

  get homologoTotal(): number {
    return this.homologoData?.total ?? 0;
  }

  /** Total variation vs. the período homólogo, e.g. "+12,4%" (empty when not comparable) */
  get homologoVariation(): string {
    return this.isHomologoVisible
      ? this.formatVariation(this.totalPurchasesValue, this.homologoTotal)
      : '';
  }

  get homologoVariationIsPositive(): boolean {
    return this.totalPurchasesValue >= this.homologoTotal;
  }

  /** "+12,4%" / "-3,0%"; empty when there is no previous value to compare with */
  formatVariation(current: number | null | undefined, previous: number | null | undefined): string {
    if (current == null || !previous) return '';
    const pct = ((current - previous) / previous) * 100;
    return (pct >= 0 ? '+' : '') + pct.toFixed(1).replace('.', ',') + '%';
  }

  /** Year of a given bar, read from the raw "Jan2026"-style month key */
  private chartPointYear(datasetIndex: number, dataIndex: number): string {
    const rows = datasetIndex === 0
      ? this.statsSearchResults?.annualConsumptionResults
      : this.homologoData?.monthlyResults;
    return (rows?.[dataIndex]?.monthOfTheYear.match(/\d{4}$/) ?? [''])[0];
  }

  private yearsOf(rows?: AnnualConsumptionLineResult[]): string[] {
    return Array.from(
      new Set(
        (rows ?? [])
          .map((r) => (r.monthOfTheYear.match(/\d{4}$/) ?? [''])[0])
          .filter(Boolean)
      )
    );
  }

  private seriesLabel(rows?: AnnualConsumptionLineResult[]): string {
    const years = this.yearsOf(rows);
    if (!years.length) return '';
    return years.length === 1 ? years[0] : `${years[0]}/${years[years.length - 1].slice(-2)}`;
  }

  // ── Top-10 legend getters ─────────────────────────────────────────────────

  get top10ProductsLegend(): { label: string; value: number; color: string }[] {
    if (!this.statsSearchResults?.topProductsResults) return [];
    return [...this.statsSearchResults.topProductsResults]
      .sort((a, b) => b.valueCashed - a.valueCashed)
      .slice(0, 10)
      .map((p, i) => ({
        label: p.designation,
        value: p.valueCashed,
        color: this.TOP_PRODUCTS_COLORS[i] ?? '#ccc',
      }));
  }

  get top10LabsLegend(): { label: string; value: number; color: string }[] {
    if (!this.statsSearchResults?.topLaboratoriesResults) return [];
    return [...this.statsSearchResults.topLaboratoriesResults]
      .map((l) => ({
        label: l.laboratoryName ?? l.designation,
        value: l.valueCashed,
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10)
      .map((l, i) => ({ ...l, color: this.TOP_LABS_COLORS[i] ?? '#ccc' }));
  }

  get defaultTopProduct(): BaseStatsLineResult | undefined {
    return this.statsSearchResults?.defaultProductResult ?? undefined;
  }

  get defaultTopLab(): { label: string; value: number } | undefined {
    const lab = this.statsSearchResults?.defaultLaboratoryResult;
    if (!lab) return undefined;
    return { label: lab.laboratoryName ?? lab.designation, value: lab.valueCashed };
  }

  get consumptionTableData(): BaseStatsLineResult[] {
    return this.statsSearchResults?.detailedConsumptionResults ?? [];
  }

  // ── Export helpers ────────────────────────────────────────────────────────

  private get exportFileNamePrefix(): string {
    return this.loggedUser.clientId ? `Cliente ${this.loggedUser.clientId} - ` : '';
  }

  DownloadAnnualConsumptionData() {
    // With a período homólogo on the chart, export both series side by side
    if (this.isHomologoVisible && this.homologoPeriod && this.statsSearchResults?.annualConsumptionResults?.length) {
      const rows = this.TransformAnnualDataWithHomologoForExcel(
        this.statsSearchResults.annualConsumptionResults,
        this.homologoData!.monthlyResults,
        this.homologoPeriod
      );
      this.ExportChartToExcel(
        rows,
        `Consumo Anual vs ${this.homologoPeriod}`,
        `${this.exportFileNamePrefix}Consumos ${this.previousSearchData.year} - Periodo Homologo ${this.homologoPeriod}.xlsx`
      );
      return;
    }

    if (this.statsSearchResults?.annualConsumptionResults?.length) {
      const transformed = this.TransformAnnualDataForExcel(
        this.statsSearchResults.annualConsumptionResults
      );
      this.ExportChartToExcel(
        transformed,
        'Consumo Anual',
        `${this.exportFileNamePrefix}Consumos ${this.previousSearchData.year}.xlsx`
      );
    }
  }

  async DownloadConsumptionTable() {

    this.isTableLoading = true;

    try {
      const isCompleteView = this.consumptionViewMode === 'completa';
      const viewLabel = isCompleteView ? 'Vista Completa' : 'Vista Simples';

      const request: PaginatedDetailedConsumptionRequest = {
        userId: this.loggedUser.userId,
        clientEntityId: this.previousSearchData.clientEntityId,
        productId: this.previousSearchData.productId,
        productIds: this.previousSearchData.productIds ?? [],
        laboratoryId: this.previousSearchData.laboratoryId,
        addressId: this.previousSearchData.addressId,
        startDate: this.previousSearchData.startDate,
        endDate: this.previousSearchData.endDate,
        year: this.previousSearchData.year,
        viewType: isCompleteView ? 'complete' : 'simple',
        subClientId: this.previousSearchData.subClientId ?? '',
        pageNumber: 1,
        pageSize: 100000,
      };

      const data = await firstValueFrom(
        this.statisticsService.GetPaginatedDetailedConsumption(request)
      );

      if (!data?.items?.length) {
        this.snackBarService.openSnackBar(
          'Sem dados para exportar',
          '#F97066',
          '../../../assets/Icons/ErrorIcon.png'
        );
        return;
      }

      const rows = this.BuildConsumptionExcelRows(data.items, isCompleteView);

      const fileName = `${this.exportFileNamePrefix}Consumos Detalhados - ${viewLabel}.xlsx`;
      const sheetName = `Detalhados - ${viewLabel}`;

      this.ExportChartToExcel(rows, sheetName, fileName);
    } catch (error) {
      console.error('Export error:', error);
      this.snackBarService.openSnackBar(
        'Erro ao exportar consumo detalhado',
        '#F97066',
        '../../../assets/Icons/ErrorIcon.png'
      );
    } finally {
      this.isTableLoading = false;
    }
  }

  /**
   * Builds the 2D array (header row + data rows) for the consumption
   * detail Excel export. Column set matches the UI table for the
   * chosen view mode (simples / completa).
   */
  private BuildConsumptionExcelRows(
    items: BaseStatsLineResult[],
    isCompleteView: boolean
  ): any[][] {
    const isLab = this.loggedUser.role === 'Laboratorio';

    // Header
    const header: string[] = isCompleteView ? ['Código', 'EAN', 'Designação'] : ['Código', 'Designação'];

    if (isCompleteView) {
      header.push('Data doc.', 'Nº doc');
    }

    header.push('Qtd do Produto Faturada', 'Qtd do Produto Bónus');

    if (isCompleteView && !isLab) {
      header.push('Preço unitário (€)', 'Desc. 1 (%)', 'Desc. 2 (%)', 'Desc. 3 (%)');
    }

    if (!isLab) {
      header.push('Valor do Produto Faturado (€)');
    }

    header.push(isLab ? 'Cliente' : 'Laboratório');

    if (isCompleteView) {
      header.push('Morada LE', 'Localidade LE');
    }

    // Data rows
    const rows: any[][] = [header];

    items.forEach((row) => {
      // EAN stays a string so Excel keeps leading zeros
      const line: any[] = isCompleteView
        ? [row.reference ?? '', row.ean ?? '', row.designation ?? '']
        : [row.reference ?? '', row.designation ?? ''];

      if (isCompleteView) {
        line.push(row.documentDate ?? '', row.documentNumber ?? '');
      }

      line.push(row.quantityCashed ?? 0, row.quantityBonus ?? 0);

      if (isCompleteView && !isLab) {
        const num = (v: number | null | undefined) => (v == null ? '' : Number(v));
        line.push(num(row.unitPrice), num(row.discount1), num(row.discount2), num(row.discount3));
      }

      if (!isLab) {
        // Keep as a number so Excel treats it as a number, not text
        line.push(Number(row.valueCashed ?? 0));
      }

      line.push(row.laboratoryName ?? '');

      if (isCompleteView) {
        line.push(row.deliveryAddress ?? '', row.deliveryLocality ?? '');
      }

      rows.push(line);
    });

    return rows;
  }

  DownloadTop10Products() {
    const data = this.top10ProductsLegend;
    if (!data.length) return;
    const rows: any[][] = [['Designação', 'Valor (€)']];
    data.forEach((d) => rows.push([d.label, d.value]));
    this.ExportChartToExcel(rows, 'Top 10 Produtos', `${this.exportFileNamePrefix}Top 10 Produtos.xlsx`);
  }

  DownloadTop10Labs() {
    const data = this.top10LabsLegend;
    if (!data.length) return;
    const rows: any[][] = [['Laboratório', 'Valor (€)']];
    data.forEach((d) => rows.push([d.label, d.value]));
    this.ExportChartToExcel(rows, 'Top 10 Laboratórios', `${this.exportFileNamePrefix}Top 10 Laboratorios.xlsx`);
  }

  // ── Chart helpers ─────────────────────────────────────────────────────────

  RemoveChartData(chart: any) {
    if (chart) {
      chart.data.labels.length = 0;
      if (chart.data.datasets[0] != null) chart.data.datasets[0].data = [];
      chart.update();
    }
  }

  /**
   * Replaces the entire dataset[0] of a chart.
   * Use for charts where styling (e.g. pie palettes) varies with data.
   */
  AddChartData(chart: any, labels: any, dataset: any) {
    if (chart) {
      chart.data.labels = labels;
      chart.data.datasets[0] = dataset;
      chart.update();
      // The pie charts are sized from the parent on creation. On mobile the
      // parent's width may still be transient when the chart is built, so
      // re-measure once data is in and the layout has settled.
      requestAnimationFrame(() => chart.resize());
    }
  }

  /**
   * Updates only the labels and data values of a chart, preserving the
   * dataset's existing styling (borderColor, backgroundColor gradient,
   * fill, tension, etc.). Use this when the visual config is set once
   * at init time and should survive data refreshes.
   */
  UpdateChartDataOnly(chart: any, labels: any[], data: number[]) {
    if (chart) {
      chart.data.labels = labels;
      if (chart.data.datasets[0]) {
        chart.data.datasets[0].data = data;
      }
      chart.update();
    }
  }

  // ── Excel helpers ─────────────────────────────────────────────────────────

  ExportTableToExcel(table: any, filename: string) {
    const worksheet = XLSX.utils.table_to_sheet(table);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    XLSX.writeFile(workbook, filename + '.xlsx');
  }

  ExportChartToExcel(data: any[], sheetName: string, fileName: string) {
    const worksheet = XLSX.utils.aoa_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    XLSX.writeFile(workbook, fileName);
  }

  TransformAnnualDataForExcel(data: AnnualConsumptionLineResult[]): any[] {
    const transformed: any[][] = [['Mês', 'Valor (€)']];
    data.forEach((item) => transformed.push([item.monthOfTheYear, item.amount]));
    return transformed;
  }

  /**
   * Current period + período homólogo, month by month (paired by index),
   * plus a totals row. Columns: Mês | Valor (€) | Valor N-1 (€) (or N-2).
   */
  TransformAnnualDataWithHomologoForExcel(
    current: AnnualConsumptionLineResult[],
    previous: AnnualConsumptionLineResult[],
    period: HomologousPeriod
  ): any[] {
    const round2 = (v: number) => Math.round(v * 100) / 100;

    const rows: any[][] = [['Mês', 'Valor (€)', `Valor ${period} (€)`]];

    let totalCurrent = 0;
    let totalPrevious = 0;
    current.forEach((item, i) => {
      const cur = Number(item.amount ?? 0);
      const prev = Number(previous[i]?.amount ?? 0);
      totalCurrent += cur;
      totalPrevious += prev;
      rows.push([item.monthOfTheYear, cur, prev]);
    });

    rows.push(['Total', round2(totalCurrent), round2(totalPrevious)]);

    return rows;
  }

  TransformTopStatsForExcel(data: BaseStatsLineResult[]): any[] {
    const transformed: any[][] = [['Designação', 'Valor Faturado (€)']];
    data.forEach((item) => transformed.push([item.designation, item.valueCashed]));
    return transformed;
  }

  // ── UI events ─────────────────────────────────────────────────────────────

  toggleMenu(): void {
    this.isMenuOpen = !this.isMenuOpen;
  }

  closeMenu(): void {
    this.isMenuOpen = false;
  }

  selectedClient(event: any) {
    this.addressSelected = undefined;
    this.clientSelected = event.option.value;
  }

  displayFn(user: User): string {
    return user && user.name ? `${user.clientNumber} - ${user.name}` : '';
  }

  private _filter(value: string): User[] {
    const filterValue = value.toLowerCase();
    return this.users.filter(
      (option) =>
        option.name.toLowerCase().includes(filterValue) ||
        option.clientNumber.includes(filterValue)
    );
  }

  /** Prevents Chart.js from overriding the canvas display style */
  private _preventChartDisplayOverride(el: HTMLCanvasElement) {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          const style = el.getAttribute('style');
          if (style?.includes('display') && style !== '') {
            el.style.display = '';
          }
        }
      });
    });
    observer.observe(el, { attributes: true });
  }


  // ── Pagination state ──────────────────────────────────────────────────────

  get totalPages(): number {
    const total = this.statsSearchResults?.detailedConsumptionTotalCount ?? 0;
    return Math.max(1, Math.ceil(total / this.pageSize));
  }

  goToPage(page: number) {
    if (page < 1 || page > this.totalPages || page === this.currentPage) return;
    this.FetchTablePage(page);
  }

  setConsumptionView(mode: 'simples' | 'completa') {
    this.consumptionViewMode = mode;
    this.currentPage = 1;
    this.FetchTablePage(1);
  }

  get pageNumbers(): number[] {
    const total = this.totalPages;
    // For many pages, show a windowed range (current ± 2)
    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }
    const pages: number[] = [];
    const start = Math.max(1, this.currentPage - 2);
    const end = Math.min(total, this.currentPage + 2);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }

  private FetchTablePage(pageNumber: number) {
    this.isTableLoading = true;

    const request: PaginatedDetailedConsumptionRequest = {
      userId: this.loggedUser.userId,
      clientEntityId: this.previousSearchData.clientEntityId,
      productId: this.previousSearchData.productId,
      productIds: this.previousSearchData.productIds ?? [],
      laboratoryId: this.previousSearchData.laboratoryId,
      addressId: this.previousSearchData.addressId,
      startDate: this.previousSearchData.startDate,
      endDate: this.previousSearchData.endDate,
      year: this.previousSearchData.year,
      viewType: this.consumptionViewMode === 'completa' ? 'complete' : 'simple',
      subClientId: this.previousSearchData.subClientId ?? '',
      pageNumber,
      pageSize: this.pageSize,
    };

    this.tablePageSub?.unsubscribe();
    this.tablePageSub = this.statisticsService.GetPaginatedDetailedConsumption(request).subscribe({
      next: (data: PaginatedDetailedConsumptionResponse) => {
        // Patch only the paginated fields on the existing results object.
        // All other charts/summaries stay untouched.
        if (this.statsSearchResults) {
          this.statsSearchResults = {
            ...this.statsSearchResults,
            detailedConsumptionResults: data.items,
            detailedConsumptionTotalCount: data.totalCount,
          };
        }
        this.currentPage = data.pageNumber;
      },
      error: (error: unknown) => {
        this.isTableLoading = false;
        console.error('Error fetching page:', error);
      },
      complete: () => {
        this.isTableLoading = false;
      },
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe.unsubscribe();
    this.searchStatsSub?.unsubscribe();
    this.tablePageSub?.unsubscribe();
    this.homologoSub?.unsubscribe();
  }
}